import { HttpClient } from '@angular/common/http';
import { Directive } from '@angular/core';
import { BehaviorSubject, Observable, catchError, finalize, map, of, shareReplay, tap, throwError } from 'rxjs';
import {
  ActivateAccountRequest,
  ActivateAccountResponse,
  AuthTokens,
  ChangeLanguageResponse,
  ChangePasswordRequest,
  ForgotPasswordRequest,
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  RegisterResponse,
  ResetPasswordRequest,
  SmsSendCodeRequest,
  SmsStatusResponse,
  SmsVerifyCodeRequest,
} from './interfaces/auth.interfaces';

/**
 * Base auth logic shared by every app (admin/company/executor/client).
 * Each app's own AuthService extends this with its own `User` shape,
 * its own `authEndpoint` (e.g. `/api/company/auth`, `/api/executors/auth`)
 * and overrides `onAfterLogin`/`onLogout` for app-specific side effects
 * (status/currency preload, websocket disconnect, impersonation cleanup,
 * etc.) instead of re-implementing login/token storage/refresh from
 * scratch. Methods an app doesn't need (e.g. admin never calls
 * `smsSendCode`) simply go unused - no harm in them being inherited.
 *
 * `@Directive()` with no selector is the standard Angular pattern for a
 * class other classes extend via DI (`@Injectable` subclasses work fine
 * extending it); it keeps Angular's DI metadata happy without this base
 * itself being provided anywhere.
 */
@Directive()
export abstract class BaseAuthService<TUser> {
  /** Refresh this long before the access token expires. */
  private static readonly REFRESH_THRESHOLD_MS = 5 * 60 * 1000;

  private proactiveRefresh$: Observable<void> | null = null;

  protected readonly currentUserSubject = new BehaviorSubject<TUser | null>(null);
  public readonly currentUser$ = this.currentUserSubject.asObservable();

  protected readonly isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  public readonly isAuthenticated$ = this.isAuthenticatedSubject.asObservable();

  protected constructor(
    protected readonly http: HttpClient,
    /** e.g. `${environment.apiUrl}/company/auth` */
    protected readonly authEndpoint: string,
  ) {
    this.loadUserFromStorage();
  }

  // ============================================
  // Overridable hooks for app-specific behavior
  // ============================================

  /** Called after a successful login/activate/register with a fresh session. */
  protected onAfterLogin(_user: TUser, _tokens: AuthTokens): void {
    // no-op by default
  }

  /** Called right before auth data is cleared (logout, forced logout, failed refresh). */
  protected onBeforeLogout(): void {
    // no-op by default
  }

  // ============================================
  // Core flows
  // ============================================

  login(credentials: LoginRequest): Observable<LoginResponse<TUser>> {
    return this.http.post<LoginResponse<TUser>>(`${this.authEndpoint}/login`, credentials).pipe(
      tap((response) => {
        if (response.success && response.data) {
          this.setAuthData(response.data.user, response.data.tokens);
        }
      }),
    );
  }

  register(data: RegisterRequest): Observable<RegisterResponse<TUser>> {
    return this.http.post<RegisterResponse<TUser>>(`${this.authEndpoint}/register`, data).pipe(
      tap((response) => {
        if (response.success && response.data) {
          this.setAuthData(response.data.user, response.data.tokens);
        }
      }),
    );
  }

  smsSendCode(data: SmsSendCodeRequest): Observable<{ success: boolean; message?: string }> {
    return this.http.post<{ success: boolean; message?: string }>(`${this.authEndpoint}/sms/send-code`, data);
  }

  smsVerifyCode(data: SmsVerifyCodeRequest): Observable<LoginResponse<TUser>> {
    return this.http.post<LoginResponse<TUser>>(`${this.authEndpoint}/sms/verify-code`, data).pipe(
      tap((response) => {
        if (response.success && response.data) {
          this.setAuthData(response.data.user, response.data.tokens);
        }
      }),
    );
  }

  smsStatus(phone: string): Observable<SmsStatusResponse> {
    return this.http.get<SmsStatusResponse>(`${this.authEndpoint}/sms/status`, { params: { phone } });
  }

  activateAccount(data: ActivateAccountRequest): Observable<ActivateAccountResponse<TUser>> {
    return this.http.post<ActivateAccountResponse<TUser>>(`${this.authEndpoint}/activate`, data).pipe(
      tap((response) => {
        if (response.success && response.data) {
          this.setAuthData(response.data.user, response.data.tokens);
        }
      }),
    );
  }

  logout(): Observable<boolean> {
    const accessToken = this.getAccessToken();
    if (!accessToken) {
      this.onBeforeLogout();
      this.clearAuthData();
      return of(true);
    }
    return this.http.post(`${this.authEndpoint}/logout`, {}).pipe(
      map(() => {
        this.onBeforeLogout();
        this.clearAuthData();
        return true;
      }),
      catchError(() => {
        this.onBeforeLogout();
        this.clearAuthData();
        return of(true);
      }),
    );
  }

  forgotPassword(data: ForgotPasswordRequest): Observable<{ success: boolean; message?: string }> {
    return this.http.post<{ success: boolean; message?: string }>(`${this.authEndpoint}/forgot-password`, data);
  }

  resetPassword(data: ResetPasswordRequest): Observable<{ success: boolean; message?: string }> {
    return this.http.post<{ success: boolean; message?: string }>(`${this.authEndpoint}/reset-password`, data);
  }

  changePassword(data: ChangePasswordRequest): Observable<{ success: boolean; message?: string }> {
    return this.http.put<{ success: boolean; message?: string }>(`${this.authEndpoint}/change-password`, data);
  }

  changeLanguage(language: string): Observable<ChangeLanguageResponse<TUser>> {
    return this.http.post<ChangeLanguageResponse<TUser>>(`${this.authEndpoint}/language`, { language }).pipe(
      tap((response) => {
        if (response.success && response.data) {
          const currentUser = this.currentUserValue;
          if (currentUser) {
            const updatedUser = { ...currentUser, lang: language } as TUser;
            this.currentUserSubject.next(updatedUser);
            localStorage.setItem('currentUser', JSON.stringify(updatedUser));
          }
          localStorage.setItem('language', language);
        }
      }),
    );
  }

  getCurrentUser(): Observable<TUser | null> {
    return this.http.get<{ success: boolean; data: TUser }>(`${this.authEndpoint}/profile`).pipe(
      map((response) => {
        if (response.success && response.data) {
          this.currentUserSubject.next(response.data);
          return response.data;
        }
        return null;
      }),
      catchError(() => of(null)),
    );
  }

  refreshToken(): Observable<AuthTokens> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return throwError(() => new Error('No refresh token available'));
    }
    return this.http.post<ApiEnvelopeLike<AuthTokens>>(`${this.authEndpoint}/token/refresh`, { refreshToken }).pipe(
      map((response) => {
        if (response.success && response.data) {
          this.setTokens(response.data);
          return response.data;
        }
        throw new Error('Failed to refresh token');
      }),
    );
  }

  /**
   * Proactive refresh: reads the real expiry from the access token's own
   * `exp` claim and refreshes when less than REFRESH_THRESHOLD_MS remains.
   * Never fails the caller - if the refresh doesn't work the request still
   * goes out and the interceptor handles the resulting 401 as before.
   */
  ensureTokenValid(): Observable<void> {
    const token = this.getAccessToken();
    const refreshToken = this.getRefreshToken();
    if (!token || !refreshToken) {
      return of(void 0);
    }
    const expiresAt = this.getAccessTokenExpiry(token);
    if (expiresAt === null || expiresAt - Date.now() > BaseAuthService.REFRESH_THRESHOLD_MS) {
      return of(void 0);
    }
    if (!this.proactiveRefresh$) {
      this.proactiveRefresh$ = this.refreshToken().pipe(
        map(() => void 0),
        catchError(() => of(void 0)),
        finalize(() => {
          this.proactiveRefresh$ = null;
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    }
    return this.proactiveRefresh$;
  }

  private getAccessTokenExpiry(token: string): number | null {
    try {
      const payload = token.split('.')[1];
      if (!payload) return null;
      const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
      const decoded = JSON.parse(atob(normalized)) as { exp?: number };
      return decoded.exp ? decoded.exp * 1000 : null;
    } catch {
      return null;
    }
  }

  // ============================================
  // Storage management
  // ============================================

  protected setAuthData(user: TUser, tokens: AuthTokens): void {
    this.currentUserSubject.next(user);
    this.isAuthenticatedSubject.next(true);
    localStorage.setItem('currentUser', JSON.stringify(user));
    this.setTokens(tokens);
    // A referral code captured from ?refCode= (see each app's AppComponent)
    // is only meant to live "until the user logs in" - this is the one
    // place both register() and login() success converge, on every app.
    try {
      localStorage.removeItem('referralCode');
    } catch {
      // private-mode/blocked storage - non-fatal
    }
    this.onAfterLogin(user, tokens);
  }

  protected setTokens(tokens: AuthTokens): void {
    localStorage.setItem('access_token', tokens.access_token);
    localStorage.setItem('refresh_token', tokens.refresh_token);
    localStorage.setItem('token_expires_in', tokens.expires_in.toString());
  }

  protected clearAuthData(): void {
    this.currentUserSubject.next(null);
    this.isAuthenticatedSubject.next(false);
    localStorage.removeItem('currentUser');
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('token_expires_in');
  }

  /** Clear tokens without an API call (used by the auth interceptor on a hard 401). */
  clearTokens(): void {
    this.onBeforeLogout();
    this.clearAuthData();
  }

  private loadUserFromStorage(): void {
    const userJson = localStorage.getItem('currentUser');
    const accessToken = localStorage.getItem('access_token');
    if (userJson && accessToken) {
      try {
        const user = JSON.parse(userJson) as TUser;
        this.currentUserSubject.next(user);
        this.isAuthenticatedSubject.next(true);
      } catch {
        this.clearAuthData();
      }
    }
  }

  // ============================================
  // Getters
  // ============================================

  get currentUserValue(): TUser | null {
    return this.currentUserSubject.value;
  }

  get isAuthenticated(): boolean {
    return this.isAuthenticatedSubject.value;
  }

  getAccessToken(): string | null {
    return localStorage.getItem('access_token');
  }

  getRefreshToken(): string | null {
    return localStorage.getItem('refresh_token');
  }

  /** Every app's own TUser has an `id` - used by shared-chat's ChatAuthLike to tell "me" apart from other participants. */
  getCurrentUserId(): number | null {
    return (this.currentUserValue as unknown as { id?: number } | null)?.id ?? null;
  }
}

interface ApiEnvelopeLike<T> {
  success: boolean;
  data: T;
}
